import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { Row } from "primereact/row";
import { useMemo, useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";

import { IProviderRemainingAmountRow } from "../PaymentDetails";

const StyledDataTable = styled(DataTable)`
  margin-top: ${sizer(6)};
  width: 100%;

  tr {
    background: var(--gray-50);
  }

  span.p-column-title {
    font-weight: var(--font-weight-semibold);
    font-size: var(--fontsize-contents);
    color: var(--color-black-4);
  }
  .p-rowgroup-header {
    display: none;
  }

  td {
    font-weight: var(--font-weight-medium);
    font-size: var(--fontsize-contents);
    color: var(--color-matte-black);
  }
`;

const firstColumnTemplate = (row: IProviderRemainingAmountRow) => {
  return <span className="font-bold">{row.providerName}</span>;
};

const remainingAmountTemplate = (
  row: IProviderRemainingAmountRow,
  onValueChange: any
) => {
  return (
    <InputNumber
      value={centsToDollars(row.totalExpectedTransferAmount)}
      currency="USD"
      locale="en-US"
      mode="currency"
      onValueChange={({ value }) => onValueChange(value, row)}
      minFractionDigits={2}
      maxFractionDigits={2}
      min={0}
      aria-autocomplete="none"
    />
  );
};

const providerPriorityTemplate = (
  row: IProviderRemainingAmountRow,
  onPriorityChange: any
) => {
  return (
    <InputNumber
      value={row.providerPriority}
      onValueChange={({ value }) => onPriorityChange(value, row)}
      min={1}
      max={999}
      showButtons={false}
      aria-autocomplete="none"
    />
  );
};

const footerGroupForPayments = (
  updatedRemainingTotal: {
    total: number;
    subtotal: number;
  },
  initialPaymentPlanRemainingAmount: number,
  remainingInstallments: number | null,
  hasChanges: boolean,
  isGFE: boolean,
  clearPatientFeePercentage: number | undefined,
  calculatePaymentInfo: (
    currentAmount: number,
    initialAmount: number,
    currentInstallments: number,
    hasChanges: boolean
  ) => { monthlyPaymentInfo: string | null; recommendationInfo: string | null }
) => {
  const currentAmountText = parsePrice(
    centsToDollars(updatedRemainingTotal.total)
  );
  const previousAmountText = parsePrice(
    centsToDollars(initialPaymentPlanRemainingAmount)
  );
  const footerText = hasChanges
    ? `${currentAmountText} (previously ${previousAmountText})`
    : currentAmountText;

  // Use the shared calculation function
  const { monthlyPaymentInfo, recommendationInfo } =
    remainingInstallments && remainingInstallments > 0
      ? calculatePaymentInfo(
          updatedRemainingTotal.total,
          initialPaymentPlanRemainingAmount,
          remainingInstallments,
          hasChanges
        )
      : { monthlyPaymentInfo: null, recommendationInfo: null };

  // Show subtotal for non-GFE orders OR GFE orders with clear patient fee
  const shouldShowSubtotal =
    !isGFE ||
    (isGFE && clearPatientFeePercentage && clearPatientFeePercentage > 0);

  return (
    <ColumnGroup>
      {shouldShowSubtotal && (
        <Row>
          <Column
            footer="Subtotal Amount"
            colSpan={3}
            footerStyle={{ textAlign: "right" }}
          />
          <Column
            footer={parsePrice(centsToDollars(updatedRemainingTotal.subtotal))}
          />
        </Row>
      )}
      <Row>
        <Column
          footer={
            clearPatientFeePercentage && clearPatientFeePercentage > 0
              ? `Remaining Amount with fee ${clearPatientFeePercentage}%`
              : "Remaining Amount"
          }
          colSpan={3}
          footerStyle={{ textAlign: "end" }}
        />
        <Column footer={footerText} />
      </Row>
      {monthlyPaymentInfo && (
        <Row>
          <Column
            footer="Monthly payment amount"
            colSpan={3}
            footerStyle={{ textAlign: "end" }}
            className="text-md"
          />
          <Column footer={monthlyPaymentInfo} className="text-md" />
        </Row>
      )}
      {recommendationInfo && (
        <Row>
          <Column
            footer="Recommendation:"
            colSpan={3}
            footerStyle={{
              textAlign: "right",
              fontSize: "0.875rem",
              fontStyle: "italic",
              fontWeight: "500",
            }}
            className="text-green-600"
          />
          <Column
            footer={recommendationInfo}
            footerStyle={{
              fontSize: "0.875rem",
              fontStyle: "italic",
              fontWeight: "500",
            }}
            className="text-green-600"
          />
        </Row>
      )}
    </ColumnGroup>
  );
};

export function RemainingAmountEditTable({
  remainingAmountBreakdown,
  differenceByProvider,
  setDifferenceByProvider,
  setValuesByProvider,
  valuesByProvider,
  clearFeePercentage,
  clearPatientFeePercentage,
  isGFE,
  initialPaymentPlanRemainingAmount,
  remainingInstallments,
  calculatePaymentInfo,
  accurateRemainingTotal,
}: {
  remainingAmountBreakdown: IProviderRemainingAmountRow[];
  differenceByProvider: { [key: string]: number };
  setDifferenceByProvider: any;
  setValuesByProvider: any;
  valuesByProvider: { [key: string]: any };
  clearFeePercentage: number;
  clearPatientFeePercentage?: number;
  isGFE: boolean;
  initialPaymentPlanRemainingAmount: number;
  remainingInstallments: number | null;
  calculatePaymentInfo: (
    currentAmount: number,
    initialAmount: number,
    currentInstallments: number,
    hasChanges: boolean
  ) => { monthlyPaymentInfo: string | null; recommendationInfo: string | null };
  accurateRemainingTotal?: number | null;
}) {
  const { setValue } = useFormContext();
  const handleAmountChange = useCallback(
    (value: number, row: IProviderRemainingAmountRow) => {
      const existingDifference = differenceByProvider[row.providerId] || 0;
      const initialValue = remainingAmountBreakdown.find(
        (provider) => provider.providerId === row.providerId
      )?.totalExpectedTransferAmount;
      const newDifference =
        dollarsToCents(value) - row.totalExpectedTransferAmount;
      setDifferenceByProvider((prev: any) => ({
        ...prev,
        [row.providerId]: existingDifference + newDifference,
      }));
      setValuesByProvider((prev: any) => ({
        ...prev,
        [row.providerId]: {
          ...prev[row.providerId],
          initialValue,
          newValue: dollarsToCents(value),
        },
      }));
    },
    [differenceByProvider]
  );

  const handlePriorityChange = useCallback(
    (value: number | null, row: IProviderRemainingAmountRow) => {
      const initialValue = remainingAmountBreakdown.find(
        (provider) => provider.providerId === row.providerId
      )?.totalExpectedTransferAmount;

      setValuesByProvider((prev: any) => ({
        ...prev,
        [row.providerId]: {
          ...prev[row.providerId],
          initialValue: prev[row.providerId]?.initialValue || initialValue,
          newValue:
            prev[row.providerId]?.newValue || row.totalExpectedTransferAmount,
          providerPriority: value,
        },
      }));
    },
    [remainingAmountBreakdown]
  );

  const updatedRemainingTotal = useMemo(() => {
    const initialRemainingAmount = remainingAmountBreakdown.reduce(
      (sum, provider) => sum + provider.totalExpectedTransferAmount,
      0
    );
    const difference = Object.values(differenceByProvider).reduce(
      (total, amount) => total + amount,
      0
    );
    const subtotal = initialRemainingAmount + difference;

    let total;

    // Check if there are any changes to the amounts
    const hasChanges = Object.values(differenceByProvider).some(
      (diff) => diff !== 0
    );

    if (isGFE && clearPatientFeePercentage && clearPatientFeePercentage > 0) {
      if (hasChanges) {
        // When there are changes, apply the percentage to the subtotal
        total = subtotal * (1 + clearPatientFeePercentage / 100);
      } else {
        // When there are no changes, use the accurate total calculated from breakdown
        // This ensures we get the exact amount with properly calculated clear patient fees
        total = accurateRemainingTotal || initialRemainingAmount;
      }
    } else if (!isGFE) {
      // For non-GFE orders, like clear package, add clear fee
      total = subtotal * (1 + clearFeePercentage / 100);
    } else {
      // For GFE orders without clear patient fee
      total = subtotal;
    }

    // if (Math.abs(initialPaymentPlanRemainingAmount - total) <= 1) {
    //   total = initialPaymentPlanRemainingAmount;
    // }

    return { total, subtotal };
  }, [
    remainingAmountBreakdown,
    differenceByProvider,
    isGFE,
    clearFeePercentage,
    clearPatientFeePercentage,
    initialPaymentPlanRemainingAmount,
    accurateRemainingTotal,
  ]);

  const tableData = useMemo(() => {
    return remainingAmountBreakdown.map((provider) => {
      const difference = differenceByProvider[provider.providerId] || 0;
      const valuesByProviderForThis = valuesByProvider[provider.providerId];
      const updatedPriority =
        valuesByProviderForThis?.providerPriority !== undefined
          ? valuesByProviderForThis.providerPriority
          : provider.providerPriority;

      return {
        ...provider,
        totalExpectedTransferAmount:
          provider.totalExpectedTransferAmount + difference,
        providerPriority: updatedPriority,
      };
    });
  }, [remainingAmountBreakdown, differenceByProvider, valuesByProvider]);

  useEffect(() => {
    setValue(
      "paymentPlanRemainingAmount",
      centsToDollars(updatedRemainingTotal.total)
    );
  }, [updatedRemainingTotal]);

  const columns = useMemo(() => {
    return [
      {
        field: "providerName",
        header: "Provider",
        body: (row: any) => firstColumnTemplate(row),
        style: { width: "30%" },
      },
      {
        field: "cptCode",
        header: "CPT Code",
        body: (row: any) => {
          return row.cptCode || "N/A";
        },
        style: { width: "20%" },
      },
      {
        field: "providerPriority",
        header: "Payment Priority",
        body: (row: IProviderRemainingAmountRow) => {
          return providerPriorityTemplate(row, handlePriorityChange);
        },
        style: { width: "20%" },
      },
      {
        field: "totalExpectedTransferAmount",
        header: "Remaining to pay",
        body: (row: IProviderRemainingAmountRow) => {
          return remainingAmountTemplate(row, handleAmountChange);
        },
        style: { width: "30%" },
      },
    ];
  }, [handleAmountChange, handlePriorityChange]);

  const hasChanges = useMemo(() => {
    return Object.values(differenceByProvider).some(
      (difference) => difference !== 0
    );
  }, [differenceByProvider]);

  return (
    <div className="flex col-12 my-3">
      <StyledDataTable
        style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
        value={tableData}
        emptyMessage="No Remaining Amounts Breakdown"
        footerColumnGroup={footerGroupForPayments(
          updatedRemainingTotal,
          initialPaymentPlanRemainingAmount,
          remainingInstallments,
          hasChanges,
          isGFE,
          clearPatientFeePercentage,
          calculatePaymentInfo
        )}
      >
        {columns.map((col: any) => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            body={col?.body}
            style={col?.style}
          />
        ))}
      </StyledDataTable>
    </div>
  );
}
